import React, { useState, useEffect, useRef } from 'react';

import { sendSupportMessage, getSupportHistory, MessageHistoryItem, closeSupportSession } from '../services/api';
import './SupportChatOverlay.css';

interface SupportChatOverlayProps {
    isOpen: boolean;
    onClose: () => void;
}

const SupportChatOverlay: React.FC<SupportChatOverlayProps> = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState<MessageHistoryItem[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleClose = async () => {
        // 1. Immediate Local Clear (Robust UI)
        setMessages([]);
        setLoading(false);
        setHistoryLoading(false);
        onClose(); // Close UI immediately

        // 2. Async Backend Close (Fire and Forget)
        try {
            await closeSupportSession();
        } catch (e) {
            // Mute error to user, just log it. 
            // The UI is already clear, so user is happy.
            console.error("Backend close session failed (silent):", e);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchHistory();

            // Robust Focus Strategy with multiple attempts
            const timers = [
                setTimeout(() => inputRef.current?.focus(), 100),
                setTimeout(() => inputRef.current?.focus(), 300),
                setTimeout(() => inputRef.current?.focus(), 600)
            ];
            return () => timers.forEach(clearTimeout);
        }
    }, [isOpen]);

    useEffect(scrollToBottom, [messages]);

    // Clean up session on unmount (redundant safety)
    useEffect(() => {
        return () => {
            // Best effort close on unmount
            closeSupportSession().catch(() => { });
        };
    }, []);

    const fetchHistory = async () => {
        try {
            setHistoryLoading(true);
            const history = await getSupportHistory();
            if (history) {
                setMessages(history);
            } else {
                setMessages([]);
            }
        } catch (error) {
            console.error('Error fetching support history:', error);
            setMessages([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMsg = input.trim();
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setInput('');
        setLoading(true);

        try {
            const res = await sendSupportMessage(userMsg);
            if (res) {
                setMessages(prev => [...prev, { role: 'assistant', content: res.response }]);
            }
        } catch (error) {
            console.error('Error sending support message:', error);
            setMessages(prev => [...prev, { role: 'assistant', content: 'Lo siento, hubo un error al conectar con soporte.' }]);
        } finally {
            setLoading(false);
            // Focus after response
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    // Observer: Refocus input when it becomes enabled again
    useEffect(() => {
        if (!loading && isOpen) {
            inputRef.current?.focus();
        }
    }, [loading, isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className="support-overlay"
            style={{
                zIndex: 9999,
                pointerEvents: 'all'
            }}
        >
            <div className="support-header">
                <div className="support-title">
                    <i className="bi bi-shield-check me-2"></i>
                    Centro de Soporte CUDA
                </div>
                <button
                    className="btn-close btn-close-white"
                    onClick={handleClose}
                    style={{ cursor: 'pointer', zIndex: 10001 }}
                ></button>
            </div>

            <div className="support-body">
                {historyLoading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status"></div>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="support-welcome">
                        <i className="bi bi-chat-dots mb-3" style={{ fontSize: '2rem', opacity: 0.5 }}></i>
                        <p>¿En qué podemos ayudarte hoy?</p>
                        <small className="text-muted">Hablarás con nuestro Agente de Soporte especializado.</small>
                    </div>
                ) : (
                    messages.map((m, i) => (
                        <div key={i} className={`support-msg-container ${m.role}`}>
                            <div className={`support-msg-bubble ${m.role}`}>
                                {m.content}
                            </div>
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="support-footer">
                <form onSubmit={handleSend} className="input-group">
                    <input
                        ref={inputRef}
                        type="text"
                        className="form-control support-input"
                        placeholder="Escribe tu duda o reclamo..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        disabled={loading}
                        autoFocus
                    />
                    <button className="btn btn-primary support-send-btn" type="submit" disabled={loading}>
                        <i className="bi bi-send-fill"></i>
                    </button>
                </form>
            </div>
        </div>
    );
};
export default SupportChatOverlay;
